import React, { useEffect, useState } from 'react';
import { Stack } from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import useDocumentTitle from '../../../hooks/useDocumentTitle';
import SalesCreditNoteRegisterForm from '../components/SalesCreditNoteRegisterForm';
import { useGetSalesCreditNoteRegisterHandler } from '../dataHandlers/getSalesCreditNoteRegisterHandler';
import { useUpdateSalesCreditNoteRegisterHandler } from '../dataHandlers/updateSalesCreditNoteRegisterHandler';
import DocumentSkeletonForm from '../../../components/DocumentSkeletonForm';
import { useTranslatedMessages } from '../../../hooks/useTranslatedMessages';
import { usePermissions } from '../../../hooks/usePermissions';
import { UPDATE_SALE_REGISTER } from '../../../constants/eInvoicePermissions';
import { useActionDeniedSnackbar } from '../../../hooks/useActionDeniedSnackbar';

export default function EditSalesCreditNoteRegisterPage() {
  const { salesInvoiceRegister } = useTranslatedMessages();
  useDocumentTitle(salesInvoiceRegister?.pages?.edit_title_credit_note ?? 'Edit Sales Credit Note Register Entry');
  const navigate = useNavigate();
  const { document_id } = useParams();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { loading, hasEInvoicePermission } = usePermissions();
  const showActionDeniedSnackbar = useActionDeniedSnackbar();
  const { register, isLoading } = useGetSalesCreditNoteRegisterHandler(document_id);

  useEffect(() => {
    if (loading) return;
    if (!hasEInvoicePermission(UPDATE_SALE_REGISTER)) {
      showActionDeniedSnackbar();
      navigate('/sales-invoice-register?tab=credit-notes', { replace: true });
    }
  }, [loading, hasEInvoicePermission, showActionDeniedSnackbar, navigate]);

  useEffect(() => {
    if (!isLoading && register && register.document_mode !== 'NON_E_INVOICE') {
      const documentId = register.uuid || register.id;
      if (register.document_mode === 'E_INVOICE') {
        navigate(`/sales/documents/credit-notes/${documentId}`, { replace: true });
      } else {
        navigate('/sales-invoice-register?tab=credit-notes', { replace: true });
      }
    }
  }, [isLoading, register, navigate]);

  const onRegisterUpdated = () => {
    navigate('/sales-invoice-register?tab=credit-notes', { replace: true });
  };

  const { onSubmit, error, isPending } = useUpdateSalesCreditNoteRegisterHandler({ onSuccess: onRegisterUpdated });

  useEffect(() => {
    if (!isPending && isSubmitting) {
      setIsSubmitting(false);
    }
  }, [isPending, isSubmitting]);

  const onSaveForm = (data) => {
    setIsSubmitting(true);
    onSubmit({ id: document_id, payload: data });
  };

  const onCancel = () => {
    navigate('/sales-invoice-register?tab=credit-notes');
  };

  if (isLoading || !register) {
    return <DocumentSkeletonForm />;
  }

  if (register.document_mode !== 'NON_E_INVOICE') {
    return <DocumentSkeletonForm />;
  }

  return (
    <Stack direction="column" sx={{ mt: '-1rem' }}>
      <SalesCreditNoteRegisterForm
        initialData={register}
        onSubmit={onSaveForm}
        onCancel={onCancel}
        apiErrors={error}
        isLoading={isPending || isSubmitting}
      />
    </Stack>
  );
}
